import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Info, Calendar, Banknote, Plane, ShieldCheck,
  Settings, UserCheck, MapPin, Gauge, ChevronDown,
  Wind, Fuel, Zap, Activity, UploadCloud, FileText,
  Trash2, Radio, Globe, Layers, Navigation, Clock
} from "lucide-react";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigation } from "@/context/NavigationContext";
import { AircraftRentalDto, BookingDto } from "@/types";
import { Select, message } from "antd";
import InstrumentLoader from "@/components/InstrumentLoader";
import TimeLogAltimeterButton from "@/components/TimeLogAltimeterButton";
import AircraftRentalCalendarModal from "@/components/AircraftRentalCalendarModal";
import {
    AVIONICS,
    EQUIPMENT,
    LICENSE_TYPES,
    AIRCRAFT_TYPES
} from "../enums";
import ConfirmationAlert from "@/components/ConfirmationAlert";

const GlassCard = ({ children, className = "", title, icon: Icon }: any) => (
  <div className={`bg-[#0F172A]/80 backdrop-blur-xl border border-slate-800 rounded-3xl p-6 shadow-2xl flex flex-col h-full ${className}`}>
    {title && (
      <div className="flex items-center gap-2 mb-5 border-b border-slate-800/50 pb-4">
        {Icon && <Icon size={18} className="text-blue-400" />}
        <h3 className="text-xs font-black uppercase tracking-[0.15em] text-white">{title}</h3>
      </div>
    )}
    <div className="flex-1">{children}</div>
  </div>
);

const InputField = "w-full h-11 px-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white placeholder:text-slate-500 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none";
const Label = "text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5 block ml-1";
const commonSelectProps = {
  variant: "borderless" as const,
  className: "w-full h-11 bg-[#1E293B] border border-[#334155] rounded-xl text-white",
  dropdownStyle: { backgroundColor: '#0F172A', border: '1px solid #334155', color: 'white' },
  styles: {
    selection: { color: 'white', paddingLeft: '8px' },
    input: { color: 'white' }
  }
};

const AircraftRental = () => {
  const { saveAircraftRental, updateAircraftRental } = useAppContext();
  const { rentalCommissionRate, syncSystemCommissionRates, getDictValue } = useAuth() as any;
  const { params, navigateTo } = useNavigation();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dailySlots, setDailySlots] = useState({});
  const [tempBookings, setTempBookings] = useState<BookingDto[]>([]);

  const isInitialMount = useRef(true);
  const rentalFromParams = params?.aircraftRental as AircraftRentalDto | undefined;
  const isEditMode = !!rentalFromParams;

  const [existingMedia, setExistingMedia] = useState<{id: number, url: string}[]>([]);
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [removedBookingIds, setRemovedBookingIds] = useState<number[]>([]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [price, setPrice] = useState<number | "">("");
  const [commissionModel, setCommissionModel] = useState("DEDUCT_FROM_PRICE");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isDraftAlertOpen, setIsDraftAlertOpen] = useState(false);

  const [formData, setFormData] = useState<Partial<AircraftRentalDto>>({
    pricingType: 'WET',
    billingMethod: 'HOBBS',
    isIfrCertified: false,
    checkOutRequired: true,
    avionics: [],
    equipment: [],
    requiredLicenceTypes: [],
    fuelType: undefined
  });

  const COMMISSION_MODELS = [
    { code: "DEDUCT_FROM_PRICE", value: "Deduce din prețul total (Comision inclus)" },
    { code: "ADD_ON_TOP", value: "Adaugă peste preț (Comision adăugat peste)" }
  ];

  const alertDict = {
      title: "Verificare Necesară",
      subtitle: isEditMode ? "Re-evaluare Flotă" : "Validare Aeronavă Închiriere",
      description: isEditMode
        ? "Ai ales să modifici datele unei aeronave din flota curentă. Pentru a menține standardele stricte de navigabilitate, orice actualizare trimite charter-ul înapoi în lista de verificare."
        : "Planul tău de zbor este aproape aprobat! Înainte ca aeronava să poată fi rezervată pe marketplace, aceasta va trece printr-o scurtă evaluare a cerințelor legale și a licențelor.",
      info: isEditMode
        ? [
            "Aeronava va fi temporar indisponibilă pentru rezervări noi pe platformă.",
            "Echipa operațională va valida noul tarif orar și intervalele de revizie ARC.",
            "După verificare, charter-ul va fi listat automat cu noile specificații."
          ]
        : [
            "Dispeceratul nostru va valida compatibilitatea licențelor acceptate.",
            "Se vor verificare datele tehnice introduse (viteza de croazieră, consumul și ICAO).",
            "Odată aprobată, calendarul de disponibilitate devine instant activ pentru piloți."
          ],
      btnCancel: "Înapoi la formular",
      btnConfirm: "Am înțeles, publică"
    };

  const draftAlertDict = {
    title: "SALVARE ÎN MOD DRAFT",
    subtitle: "Listarea nu va fi publică",
    description: "Anunțul va fi salvat în contul tău fără a fi trimis spre verificare.",
    info: [
      "Această aeronavă NU va fi vizibilă public pentru închiriere.",
      "Oricând poți reveni pentru a completa datele lipsă și a o trimite spre aprobare.",
      "Anunțul va rămâne salvat în siguranță în panoul tău de administrare."
    ],
    btnCancel: "Înapoi la editare",
    btnConfirm: "Confirmă Salvare Draft"
  };

  useEffect(() => {
    if (syncSystemCommissionRates) {
      syncSystemCommissionRates().catch((err: any) => {
        console.error("Nu s-au putut sincroniza comisioanele globale la montarea paginii:", err);
      });
    }
  }, [syncSystemCommissionRates]);

  const computedValues = useMemo(() => {
    const inputPrice = Number(price) || 0;
    const rate = Number(rentalCommissionRate) || 0;

    if (commissionModel === "DEDUCT_FROM_PRICE") {
      const grossValue = inputPrice;
      const commissionValue = grossValue * rate;
      const netValue = grossValue - commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    } else {
      const netValue = inputPrice;
      const commissionValue = netValue * rate;
      const grossValue = netValue + commissionValue;
      return { grossValue, commissionValue, netValue, rate };
    }
  }, [price, commissionModel, rentalCommissionRate]);

  const totalDays = useMemo(() => {
    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    const dates = new Set(
      timeSlots.map(slot => {
        if (!slot.from || slot.from.year === undefined || slot.from.month === undefined || slot.from.day === undefined) return "";
        return new Date(slot.from.year, slot.from.month - 1, slot.from.day).toDateString();
      }).filter(Boolean)
    );
    return dates.size;
  }, [formData.calendarPayloadDto]);

  const totalSlots = useMemo(() => {
    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    return timeSlots.reduce((acc, slot) => {
      if (!slot.from || !slot.to) return acc;
      const fromMin = (slot.from.hour || 0) * 60 + (slot.from.minute || 0);
      const toMin = (slot.to.hour || 0) * 60 + (slot.to.minute || 0);
      const diffMs = (toMin - fromMin) * 60000;
      const intervals = diffMs / (30 * 60000);
      return acc + Math.round(intervals);
    }, 0);
  }, [formData.calendarPayloadDto]);

  const hasMedia = existingMedia.length > 0 || mediaFiles.length > 0;

  const isNotEmpty = (val: any) => val !== null && val !== undefined && val.toString().trim() !== "";

  const validateRegistration = (reg) => {
    if (!reg) return false;
    return /^[A-Z]{1,3}-[A-Z0-9]{3,6}$/i.test(reg);
  };

  const checkFormValidity = () => {
    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    const mandatoryPresent =
      hasMedia &&
      isNotEmpty(formData.manufacturer) &&
      isNotEmpty(formData.model) &&
      isNotEmpty(formData.category) &&
      isNotEmpty(formData.registration) &&
      isNotEmpty(formData.baseAirport) &&
      isNotEmpty(formData.pricingType) &&
      isNotEmpty(formData.billingMethod) &&
      isNotEmpty(formData.lastMaintenanceCheck) &&
      formData.isIfrCertified !== null &&
      formData.isIfrCertified !== undefined &&
      (formData.requiredLicenceTypes && formData.requiredLicenceTypes.length > 0) &&
      (timeSlots.length > 0);

    const isPriceValid = isNotEmpty(price) && Number(price) > 0;

    return isPriceValid && mandatoryPresent && validateRegistration(formData.registration);
  };

  const isFormValid = checkFormValidity();

  useEffect(() => {
    if (rentalFromParams && isInitialMount.current) {
      setFormData(rentalFromParams);
      if (rentalFromParams.mediaUrls && rentalFromParams.mediaUrls.length > 0) {
        const mappedUrls = rentalFromParams.mediaUrls.map((urlStr, index) => ({
          id: index,
          url: urlStr
        }));
        setExistingMedia(mappedUrls);
      } else if (rentalFromParams.media) {
        const mapped = rentalFromParams.media.map((m: any, index: number) => ({
          id: index,
          url: m.mediaData.startsWith('data:') ? m.mediaData : `data:image/jpeg;base64,${m.mediaData}`
        }));
        setExistingMedia(mapped);
      }
      setCommissionModel(rentalFromParams.commissionModel || "DEDUCT_FROM_PRICE");
      if (rentalFromParams.commissionModel === "ADD_ON_TOP") {
        setPrice(rentalFromParams.netValue || "");
      } else {
        setPrice(rentalFromParams.grossValue || "");
      }
      isInitialMount.current = false;
    }
  }, [rentalFromParams]);

  useEffect(() => {
    return () => {
      setRemovedBookingIds([]);
      setTempBookings([]);
      setTouched({});
    };
  }, []);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setMediaFiles([...mediaFiles, ...Array.from(e.target.files)]);
  };

  const removeExistingImage = (id: number) => {
    setExistingMedia(prev => prev.filter(img => img.id !== id));
  };

  const removeNewFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const executeSubmit = async (isDraftMode = false) => {
    setIsAlertOpen(false);
    setIsDraftAlertOpen(false);
    setLoading(true);
    try {
          const rentalDto: AircraftRentalDto = {
            rentalId: isEditMode ? rentalFromParams?.rentalId : undefined,
            sellerId: isEditMode ? rentalFromParams?.sellerId : undefined,
            manufacturer: formData.manufacturer,
            model: formData.model,
            category: formData.category,
            registration: formData.registration,
            baseAirport: formData.baseAirport,
            isIfrCertified: formData.isIfrCertified,
            grossValue: computedValues.grossValue,
            pricingType: formData.pricingType,
            billingMethod: formData.billingMethod,
            media: mediaFiles,
            mediaUrls: existingMedia.map((m) => m.url),
            draft: isDraftMode,
            active: !isDraftMode,
            verified: false,
            aircraftRentalDetailsId: isEditMode ? rentalFromParams?.aircraftRentalDetailsId : undefined
          };

          const rentalDetailsDto: AircraftRentalDetailsDto = {
            productionYear: formData.productionYear,
            cruiseSpeed: Number(formData.cruiseSpeed),
            fuelConsumption: Number(formData.fuelConsumption),
            fuelType: formData.fuelType,
            avionics: formData.avionics || [],
            equipment: formData.equipment || [],
            commissionModel: commissionModel,
            commissionRate: computedValues.rate,
            commissionValue: computedValues.commissionValue,
            netValue: computedValues.netValue,
            requiredLicenceTypes: formData.requiredLicenceTypes || [],
            minimumTotalExperience: Number(formData.minimumTotalExperience),
            checkOutRequired: formData.checkOutRequired,
            insurance: formData.insurance,
            insuranceDeductible: Number(formData.insuranceDeductible),
            lastMaintenanceCheck: formData.lastMaintenanceCheck,
            additionalDetails: formData.additionalDetails,
            chargingModel: "BY_COMMISSION",
            calendarPayloadDto: formData.calendarPayloadDto,
          };

          if (isEditMode) {
            const success = await updateAircraftRental(rentalDto, rentalDetailsDto, removedBookingIds);
            if (success) {
              message.success("Modificări salvate.");
              setRemovedBookingIds([]);
              navigateTo("dashboard");
            } else {
              message.error("Eroare la actualizarea anunțului.");
            }
          } else {
            const success = await saveAircraftRental(rentalDto, rentalDetailsDto);
            if (success) {
              message.success("Anunț publicat.");
              navigateTo("dashboard");
            } else {
              message.error("Eroare la publicare.");
            }
          }
    } catch (err) {
      console.error(err);
      message.error("Eroare la procesare.");
    } finally {
      setLoading(false);
    }
  };

  const validateAndScroll = () => {
    setIsSubmitted(true);
    setTouched({ registration: true, bookings: true });

    if (!formData.registration || !validateRegistration(formData.registration) || !formData.manufacturer || !formData.model || !formData.category || !formData.baseAirport) {
      document.getElementById("identity-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!hasMedia) {
      document.getElementById("media-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!isNotEmpty(price) || Number(price) <= 0 || !formData.pricingType || !formData.billingMethod) {
      document.getElementById("pricing-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    if (!formData.requiredLicenceTypes || formData.requiredLicenceTypes.length === 0 || !formData.lastMaintenanceCheck) {
      document.getElementById("requirements-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    const timeSlots = formData.calendarPayloadDto?.timeSlotDtoList || [];
    if (timeSlots.length === 0) {
      document.getElementById("bookings-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
      message.error("Vă rugăm să completați toate câmpurile obligatorii.");
      return false;
    }

    return true;
  };

  const handleDraftClick = () => {
    setIsDraftAlertOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = validateAndScroll();

    if (isValid) {
      setIsAlertOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={loading} />

      <div className="container max-w-7xl py-12 px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-blue-600 pl-6">
          <div>
            <p className="text-blue-400 font-mono text-sm tracking-[0.2em] mb-1 uppercase italic font-black tracking-widest">
              FLIGHT OPERATIONS // {isEditMode ? "RENTAL_UPDATE" : "RENTAL_REGISTRATION"}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight">
              {isEditMode ? "Editare" : "Înregistrare"} <span className="text-slate-500">Aeronavă</span>
            </h1>
          </div>
          <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
            <div className="text-right px-4 border-r border-slate-800">
              <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">System</p>
              <p className="text-blue-500 font-mono text-xs font-bold uppercase">Ready</p>
            </div>
            <div className="text-right px-4">
              <p className="text-[10px] text-slate-500 uppercase font-bold">Status</p>
              <p className="text-orange-500 font-mono text-xs font-bold flex items-center gap-1">
                <Activity size={12} className="animate-pulse" /> AWAITING_INPUT
              </p>
            </div>
          </div>
        </header>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="space-y-8">
            <div id="identity-section">
              <GlassCard title="Identificare & Locație" icon={MapPin}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 grid grid-cols-2 gap-4">
                      <div>
                          <label className={Label}>Tail Number <span className="text-red-500">*</span></label>
                          <input
                              className={`${InputField} ${
                                  (touched.registration || isSubmitted) && !validateRegistration(formData.registration)
                                  ? "border-red-500 ring-1 ring-red-500"
                                  : ""
                              }`}
                              placeholder="YR-XXX"
                              value={formData.registration || ""}
                              onBlur={() => setTouched(prev => ({ ...prev, registration: true }))}
                              onChange={e => setFormData({...formData, registration: e.target.value.toUpperCase()})}
                          />
                          {(touched.registration || isSubmitted) && !validateRegistration(formData.registration) && (
                              <p className="text-[10px] text-red-500 font-bold mt-1 ml-1 uppercase">Format invalid (ex: YR-5000)</p>
                          )}
                      </div>
                      <div>
                          <label className={Label}>An Fabricație</label>
                          <Select
                                  {...commonSelectProps}
                                  showSearch
                                  placeholder={<span className="text-slate-500">Selectează An</span>}
                                  value={formData.productionYear || undefined}
                                  onChange={val => setFormData({...formData, productionYear: val})}
                                  options={Array.from({ length: 60 }, (_, i) => 2026 - i).map(y => ({
                                      label: y.toString(),
                                      value: y.toString(),
                                      style: { color: 'white' }
                                  }))}
                              />
                      </div>
                  </div>
                  <div>
                    <label className={Label}>Producător <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !isNotEmpty(formData.manufacturer) ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder="Cessna" value={formData.manufacturer || ""} onChange={e => setFormData({...formData, manufacturer: e.target.value})} />
                  </div>
                  <div>
                    <label className={Label}>Model <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !isNotEmpty(formData.model) ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder="172 Skyhawk" value={formData.model || ""} onChange={e => setFormData({...formData, model: e.target.value})} />
                  </div>
                  <div>
                    <label className={Label}>Categorie <span className="text-red-500">*</span></label>
                    <Select
                        {...commonSelectProps}
                        className={`${commonSelectProps.className} ${isSubmitted && !isNotEmpty(formData.category) ? "border-red-500 ring-1 ring-red-500" : ""}`}
                        placeholder={<span className="text-slate-500">Selectează categoria...</span>}
                        value={formData.category || undefined}
                        onChange={val => setFormData({...formData, category: val})}
                        options={AIRCRAFT_TYPES.map(cat => ({
                          label: cat.label,
                          options: cat.options.map(opt => ({ label: opt.label, value: opt.code, style: { color: 'white' } }))
                        }))}
                      />
                  </div>
                  <div>
                    <label className={Label}>Aerodrom Bază (ICAO) <span className="text-red-500">*</span></label>
                    <input className={`${InputField} ${isSubmitted && !isNotEmpty(formData.baseAirport) ? "border-red-500 ring-1 ring-red-500" : ""}`} placeholder="ex: LRBS" value={formData.baseAirport || ""} onChange={e => setFormData({...formData, baseAirport: e.target.value})} />
                  </div>
                </div>
              </GlassCard>
            </div>

            <div className="grid grid-cols-2 gap-4">
               {[
                 { label: 'Cruise Speed', unit: 'KTS', icon: Wind, key: 'cruiseSpeed', type: 'number' },
                 { label: 'Consumption', unit: 'L/H', icon: Fuel, key: 'fuelConsumption', type: 'number' },
                 { label: 'Fuel Type', unit: 'TYPE', icon: Zap, key: 'fuelType', type: 'text', placeholder: 'Avgas/Mogas' },
                 { label: 'IFR Certified', unit: 'CERT', icon: Navigation, key: 'isIfrCertified', type: 'select', required: true },
               ].map((item) => (
                 <div key={item.label} className="bg-slate-900/40 border border-slate-800 p-5 rounded-3xl hover:border-blue-500/50 transition-all">
                    <div className="flex justify-between items-start mb-2">
                        <item.icon size={18} className="text-blue-400" />
                        <span className="text-[10px] font-black text-slate-600 tracking-tighter uppercase">{item.unit}</span>
                    </div>
                    {item.type === 'select' ? (
                        <Select
                            {...commonSelectProps}
                            variant="borderless"
                            className="w-full h-11 text-xl font-black text-white"
                            value={formData.isIfrCertified ? "true" : "false"}
                            onChange={val => {
                                setFormData({...formData, isIfrCertified: val === 'true'});
                            }}
                            options={[
                                {
                                    label: <span className="text-white font-black">NO / VFR</span>,
                                    value: "false",
                                    style: { color: 'white' }
                                },
                                {
                                    label: <span className="text-white font-black">YES / IFR</span>,
                                    value: "true",
                                    style: { color: 'white' }
                                }
                            ]}
                        />
                    ) : (
                        <input
                            type={item.type}
                            className="bg-transparent text-xl font-black text-white w-full outline-none mt-1"
                            placeholder={item.placeholder || "---"}
                            value={(formData as any)[item.key] || ""}
                            onChange={e => setFormData({...formData, [item.key]: item.type === 'number' ? (e.target.value === "" ? "" : +e.target.value) : e.target.value})}
                        />
                    )}
                    <p className="text-[10px] text-slate-500 font-bold uppercase mt-1 tracking-widest">{item.label} {item.required && <span className="text-red-500">*</span>}</p>
                 </div>
               ))}
            </div>

            <GlassCard title="Sisteme & Dotări" icon={Settings}>
                <div className="grid grid-cols-2 gap-10">
                    <div>
                        <label className={Label + " mb-4 border-b border-slate-800 pb-2 flex items-center gap-2"}>
                            <Zap size={12} className="text-orange-500" /> Avionics Bus
                        </label>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            {AVIONICS.map(opt => {
                                const isActive = formData.avionics?.includes(opt);
                                return (
                                    <div key={opt} className="flex items-center justify-between p-2 rounded-xl hover:bg-white/[0.02] cursor-pointer group"
                                         onClick={() => {
                                             const cur = formData.avionics || [];
                                             setFormData({...formData, avionics: isActive ? cur.filter(x => x !== opt) : [...cur, opt]});
                                         }}>
                                        <span className={`text-[9px] font-black uppercase tracking-widest transition-all ${isActive ? 'text-orange-500' : 'text-slate-500'}`}>
                                            {opt}
                                        </span>
                                        <div className="relative flex flex-col items-center justify-center w-7 h-9">
                                            <div className="absolute inset-0 bg-[#0a0f1e] border-x border-slate-800 rounded-sm" />
                                            <div className={`absolute top-0 w-full h-0.5 transition-all duration-300 ${isActive ? 'bg-orange-500 shadow-[0_0_8px_orange]' : 'bg-transparent'}`} />
                                            <div className={`relative w-2 h-4 rounded-full transition-all duration-500 ${isActive ? 'translate-y-[-4px]' : 'translate-y-[4px]'}`}>
                                                <div className={`w-full h-full rounded-full border shadow-md ${isActive ? 'border-orange-400 bg-orange-500' : 'border-slate-600 bg-slate-700'}`} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className={Label + " mb-4 border-b border-slate-800 pb-2 flex items-center gap-2"}>
                            <Layers size={12} className="text-blue-500" /> Ship Systems
                        </label>
                        <div className="space-y-2 max-h-80 overflow-y-auto pr-2 custom-scrollbar">
                            {EQUIPMENT.map(opt => {
                                const isActive = formData.equipment?.includes(opt);
                                return (
                                    <div key={opt} className="flex items-center justify-between p-2 rounded-xl hover:bg-white/[0.02] cursor-pointer group"
                                         onClick={() => {
                                             const cur = formData.equipment || [];
                                             setFormData({...formData, equipment: isActive ? cur.filter(x => x !== opt) : [...cur, opt]});
                                         }}>
                                        <span className={`text-[9px] font-black uppercase tracking-widest transition-all ${isActive ? 'text-blue-500' : 'text-slate-500'}`}>
                                            {opt}
                                        </span>
                                        <div className="relative flex flex-col items-center justify-center w-7 h-9">
                                            <div className="absolute inset-0 bg-[#0a0f1e] border-x border-slate-800 rounded-sm" />
                                            <div className={`absolute top-0 w-full h-0.5 transition-all duration-300 ${isActive ? 'bg-blue-500 shadow-[0_0_8px_#3b82f6]' : 'bg-transparent'}`} />
                                            <div className={`relative w-2 h-4 rounded-full transition-all duration-500 ${isActive ? 'translate-y-[-4px]' : 'translate-y-[4px]'}`}>
                                                <div className={`w-full h-full rounded-full border shadow-md ${isActive ? 'border-blue-400 bg-blue-500' : 'border-slate-600 bg-slate-700'}`} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </GlassCard>

            <div id="media-section">
              <GlassCard title="Media Center" icon={UploadCloud}>
                  <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <label className={Label}>
                          Media Files <span className="text-red-500">*</span>
                        </label>
                        {!hasMedia && (
                          <span className="text-[10px] text-red-500 font-bold uppercase tracking-tight">
                            {getDictValue("aircraft.rental.media.mandatory")}
                          </span>
                        )}
                      </div>

                      <label className={`group relative border-2 border-dashed transition-all rounded-3xl p-12 flex flex-col items-center justify-center cursor-pointer bg-slate-900/20 overflow-hidden ${
                        isSubmitted && !hasMedia
                          ? "border-red-500/80 bg-red-500/5 shadow-[0_0_15px_rgba(239,68,68,0.1)]"
                          : "border-slate-700 hover:border-orange-500/50"
                      }`}>
                          <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity">
                              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] border border-orange-500 rounded-full animate-ping" />
                          </div>
                          <div className="relative z-10 flex flex-col items-center">
                              <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                  <UploadCloud className="w-8 h-8 text-orange-500" />
                              </div>
                              <h4 className="text-white font-bold mb-1 uppercase tracking-tighter">Scan & Upload Media</h4>
                              <p className="text-xs text-slate-500 tracking-widest font-bold uppercase">Imagini sau Video (MAX 50MB)</p>
                          </div>
                          <input type="file" multiple className="hidden" onChange={handleMediaChange} />
                      </label>

                      {(existingMedia.length > 0 || mediaFiles.length > 0) && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {existingMedia.map((img) => (
                            <div key={`old-${img.id}`} className="group relative bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden aspect-video shadow-lg">
                              <img src={img.url} className="w-full h-full object-cover opacity-80" />
                              <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-blue-600 text-[7px] font-black rounded text-white uppercase">Activă</div>
                              <button type="button" onClick={() => removeExistingImage(img.id)} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg"><Trash2 size={12} /></button>
                            </div>
                          ))}
                          {mediaFiles.map((file, i) => (
                            <div key={`new-${i}`} className="group relative bg-slate-800 border border-orange-500/30 rounded-2xl aspect-video overflow-hidden shadow-lg">
                              <img src={URL.createObjectURL(file)} className="w-full h-full object-cover opacity-60" />
                              <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-orange-600 text-[7px] font-black rounded text-white uppercase">Upload</div>
                              <button type="button" onClick={() => removeNewFile(i)} className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-red-600 text-white rounded-lg"><Trash2 size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
              </GlassCard>
            </div>
          </div>

          <div className="space-y-8">
            <div id="pricing-section">
              <GlassCard title="Tarif & Gestiune Financiară" icon={Banknote}>
                  <div className="space-y-6">

                      <div>
                          <label className={Label}>Model Comisionare <span className="text-red-500">*</span></label>
                          <Select
                              {...commonSelectProps}
                              value={commissionModel}
                              onChange={val => setCommissionModel(val)}
                              options={COMMISSION_MODELS.map(m => ({
                                  label: <span className="text-white">{m.value}</span>,
                                  value: m.code
                              }))}
                          />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-slate-900/40 p-4 rounded-2xl border border-slate-800 items-center">
                          <div className="md:col-span-2">
                              <label className={Label}>
                                  {commissionModel === "DEDUCT_FROM_PRICE" ? "Tarif Orar Brut Public (EUR)" : "Tarif Orar Net Dorit (EUR)"} <span className="text-red-500">*</span>
                              </label>
                              <input
                                  type="number"
                                  className={`bg-transparent text-4xl font-black text-white w-full outline-none placeholder:text-slate-700 ${isSubmitted && (!isNotEmpty(price) || Number(price) <= 0) ? "text-red-500" : ""}`}
                                  placeholder="000"
                                  value={price}
                                  onChange={e => setPrice(e.target.value === "" ? "" : parseFloat(e.target.value))}
                              />
                          </div>
                          <div className="space-y-2">
                              <Select
                                  {...commonSelectProps}
                                  className={`${commonSelectProps.className} ${isSubmitted && !formData.pricingType ? "border-red-500 ring-1 ring-red-500" : ""}`}
                                  placeholder="Selectează tip preț"
                                  value={formData.pricingType || undefined}
                                  onChange={val => setFormData({...formData, pricingType: val})}
                                  options={[{ label: "Tip: WET", value: "WET" }, { label: "Tip: DRY", value: "DRY" }].map(o => ({...o, style: {color: 'white'}}))}
                              />
                              <Select
                                  {...commonSelectProps}
                                  className={`${commonSelectProps.className} ${isSubmitted && !formData.billingMethod ? "border-red-500 ring-1 ring-red-500" : ""}`}
                                  placeholder="Selectează mod facturare"
                                  value={formData.billingMethod || undefined}
                                  onChange={val => setFormData({...formData, billingMethod: val})}
                                  options={[{ label: "Mod: HOBBS", value: "HOBBS" }, { label: "Mod: TACHO", value: "TACHO" }].map(o => ({...o, style: {color: 'white'}}))}
                              />
                          </div>
                      </div>

                      {Number(price) > 0 && (
                          <div className="bg-blue-600/5 border border-blue-500/10 rounded-2xl p-5 space-y-3 font-mono text-xs">
                              <div className="flex justify-between text-slate-400">
                                  <span>TARIF PUBLIC ÎNCHIRIERE (BRUT):</span>
                                  <span className="font-bold text-white text-sm">{computedValues.grossValue.toLocaleString()} EUR/h</span>
                              </div>
                              <div className="flex justify-between text-slate-400 border-b border-slate-800/60 pb-2">
                                  <span>REȚINERE COMISION STRUCTURE ({(computedValues.rate * 100).toFixed(1)}%):</span>
                                  <span className="font-bold text-red-400">-{computedValues.commissionValue.toLocaleString()} EUR/h</span>
                              </div>
                              <div className="flex items-center gap-2 bg-blue-500/10 p-3 rounded-xl text-blue-400 border border-blue-500/20">
                                  <Info size={16} className="shrink-0" />
                                  <div className="flex-1 flex justify-between items-center">
                                      <span className="font-bold text-[10px] uppercase tracking-wider">NET DE DECONTAT ÎN CONT (NET):</span>
                                      <span className="text-xl font-black text-emerald-400">{computedValues.netValue.toLocaleString()} EUR/h</span>
                                  </div>
                              </div>
                          </div>
                      )}
                  </div>
              </GlassCard>
            </div>

            <div id="requirements-section">
              <GlassCard title="Cerințe Pilot & Legal" icon={UserCheck}>
                  <div className="space-y-5">
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className={Label}>Experiență Min (HRS)</label>
                              <input type="number" className={InputField} value={formData.minimumTotalExperience || ""} placeholder="ex: 100" onChange={e => setFormData({...formData, minimumTotalExperience: e.target.value === "" ? "" : +e.target.value})} />
                          </div>
                          <div>
                              <label className={Label}>Check-out</label>
                              <Select
                                      {...commonSelectProps}
                                      value={formData.checkOutRequired ? "true" : "false"}
                                      onChange={val => setFormData({...formData, checkOutRequired: val === 'true'})}
                                              options={[
                                                  { label: "Necesar", value: "true", style: { color: 'white' } },
                                                  { label: "Nu e necesar", value: "false", style: { color: 'white' } }
                                              ]}
                                  />
                          </div>
                      </div>
                      <div>
                          <label className={Label}>Licențe Acceptate <span className="text-red-500">*</span></label>
                          <div className={`flex flex-wrap gap-2 p-2 rounded-xl transition-all ${isSubmitted && (!formData.requiredLicenceTypes || formData.requiredLicenceTypes.length === 0) ? "border border-red-500 ring-1 ring-red-500 bg-red-500/5" : ""}`}>
                              {LICENSE_TYPES.map(l => (
                                  <button key={l} type="button"
                                      onClick={() => {
                                          const cur = formData.requiredLicenceTypes || [];
                                          setFormData({...formData, requiredLicenceTypes: cur.includes(l) ? cur.filter(x => x !== l) : [...cur, l]});
                                      }}
                                      className={`text-[9px] font-bold px-2 py-1.5 rounded-lg border transition-all ${formData.requiredLicenceTypes?.includes(l) ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}>
                                      {l}
                                  </button>
                              ))}
                          </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-800">
                          <div>
                              <label className={Label}>Franșiză Casco (EUR)</label>
                              <input type="number" className={InputField} value={formData.insuranceDeductible || ""} placeholder="ex: 1500" onChange={e => setFormData({...formData, insuranceDeductible: e.target.value === "" ? "" : +e.target.value})} />
                          </div>
                          <div>
                              <label className={Label}>Data Revizie / ARC <span className="text-red-500">*</span></label>
                              <input type="date" className={`${InputField} [color-scheme:dark] ${isSubmitted && !formData.lastMaintenanceCheck ? "border-red-500 ring-1 ring-red-500" : ""}`} value={formData.lastMaintenanceCheck || ""} onChange={e => setFormData({...formData, lastMaintenanceCheck: e.target.value})} />
                          </div>
                      </div>
                      <div>
                          <label className={Label}>Companie Asigurare</label>
                          <input className={InputField} value={formData.insurance || ""} placeholder="Nume companie asigurări" onChange={e => setFormData({...formData, insurance: e.target.value})} />
                      </div>
                  </div>
              </GlassCard>
            </div>

            <GlassCard title="Informații Suplimentare" icon={FileText}>
                <textarea
                    className="w-full h-32 p-4 rounded-xl bg-[#1E293B] border border-[#334155] text-white text-sm focus:border-blue-500 outline-none resize-none transition-all"
                    placeholder="Detalii extra despre hangarare, reguli de zbor, disponibilitate..."
                    value={formData.additionalDetails || ""}
                    onChange={e => setFormData({...formData, additionalDetails: e.target.value})}
                />
            </GlassCard>

            <div id="bookings-section" className={(touched.bookings || isSubmitted) && (!(formData.calendarPayloadDto?.timeSlotDtoList?.length)) ? "ring-2 ring-red-500 rounded-2xl p-1 bg-red-500/5" : ""}>
                <TimeLogAltimeterButton
                    onClick={() => {
                        setIsModalOpen(true);
                        setTouched(prev => ({...prev, bookings: true}));
                    }}
                    totalDays={totalDays}
                    totalSlots={totalSlots}
                />
                {(touched.bookings || isSubmitted) && (!(formData.calendarPayloadDto?.timeSlotDtoList?.length)) && (
                    <p className="text-[10px] text-red-500 font-bold mt-2 ml-1 uppercase">Sistemul de programări este obligatoriu</p>
                )}
            </div>

            <AircraftRentalCalendarModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSave={(updatedRentalDetails) => {
                setFormData(prev => ({
                  ...prev,
                  calendarPayloadDto: updatedRentalDetails.calendarPayloadDto
                }));
                setTouched(prev => ({ ...prev, bookings: true }));
                setIsModalOpen(false);
              }}
              registration={formData.registration || "N/A"}
              initialRentalDetails={{
                calendarPayloadDto: formData.calendarPayloadDto
              }}
            />
          </div>

          <div className="lg:col-span-2 flex flex-col md:flex-row items-center justify-between gap-6 p-8 bg-[#0F172A]/90 border border-slate-800 rounded-3xl mt-4 shadow-2xl backdrop-blur-xl">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-900/20">
                      <ShieldCheck className="text-blue-400" size={24} />
                    </div>
                    <div>
                        <h4 className="text-white font-black uppercase text-sm italic tracking-widest">Verify & List</h4>
                        <p className="text-[10px] text-slate-400 uppercase font-bold tracking-tighter">Anunțul va fi vizibil pe marketplace după validare.</p>
                    </div>
                </div>
                <div className="flex flex-wrap md:flex-nowrap items-center gap-3 w-full md:w-auto">
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => navigateTo("dashboard")}
                      className="flex-1 md:flex-none h-12 px-6 rounded-xl font-bold uppercase text-xs text-slate-400 hover:text-white hover:bg-slate-800/60 transition-all border border-slate-800/80"
                    >
                      Abandonează
                    </Button>
                    <Button
                      type="button"
                      disabled={loading}
                      onClick={handleDraftClick}
                      className="flex-1 md:flex-none h-12 px-6 rounded-xl font-black uppercase tracking-wider text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700/80 shadow-lg active:scale-95 transition-all flex items-center gap-2"
                    >
                      <FileText size={15} className="text-slate-400" />
                      Salvează Draft
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 md:flex-none h-12 px-8 rounded-xl font-black uppercase italic tracking-wider text-xs bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white shadow-lg shadow-blue-900/40 hover:shadow-blue-600/30 active:scale-95 transition-all"
                    >
                      {isEditMode ? "Salvează schimbările" : "Verifică și publică"}
                    </Button>
                </div>
          </div>
        </form>
      </div>
      <ConfirmationAlert
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onConfirm={() => executeSubmit(false)}
        dict={alertDict}
      />
      <ConfirmationAlert
        isOpen={isDraftAlertOpen}
        onClose={() => setIsDraftAlertOpen(false)}
        onConfirm={() => executeSubmit(true)}
        dict={draftAlertDict}
      />
    </div>
  );
};

export default AircraftRental;